import { Box, Container, Grid } from "@mui/material"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useMediaQuery } from "react-responsive"
import { useLocation, useNavigate } from "react-router-dom"
import { useRecoilState } from "recoil"

import { Title } from "src/components/layout/Title"
import { useLoginContext } from "src/hooks/useLoginContext"
import { MEMBER_TYPE, RESPONSE_CODE, SNACKBAR_TYPES, SORT_ORDER } from "src/modules/const"
import { Adviser, EventService, ProfileService } from "src/openapi"
import { ShowToast } from "src/states/showToast"
import { Button, Image, Typography } from "src/UILibrary"
import { EventJoinModal } from "../event/components/EventJoinModal"
import { CareerItem } from "./components/CareerItem"
import { EventItem } from "./components/EventItem"
import "./adviser_detail.scss"

const PAGE_SIZE = 9

export const AdviserDetailPage = () => {
  const { t } = useTranslation("translation", { keyPrefix: "adviser" })

  const isPC = useMediaQuery({ query: "(min-width: 640px)" })

  const [, setShowToast] = useRecoilState(ShowToast)

  const navigate = useNavigate()

  const { loggedin, userType, getJobTitles } = useLoginContext()

  const { state } = useLocation()
  const { adviserID } = state || ""
  const { userID } = state || 0

  const [adviser, setAdviser] = useState<Adviser>()
  const [events, setEvents] = useState<Event[]>([])

  const [openJoinModal, setOpenJoinModal] = useState<boolean>(false)
  const [selectedItem, setSelectedItem] = useState<Event>()

  if (!adviserID || !userID) {
    navigate("/adviser")
  }

  useEffect(() => {
    ProfileService.adviserDetail(adviserID)
      .then((res) => {
        if (res.status === RESPONSE_CODE.SUCCESS) {
          setAdviser(res.data)
        } else {
          setShowToast({ show: true, text: t("failed_get_detail"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowToast({ show: true, text: t("failed_get_detail"), type: SNACKBAR_TYPES.ERROR })
      })

    handleGetEvents()
  }, [adviserID])

  const handleGetEvents = () => {
    EventService.listEvent(
      undefined,
      0,
      PAGE_SIZE,
      undefined,
      undefined,
      userID,
      undefined,
      undefined,
      undefined,
      SORT_ORDER.ASCENDING,
      "start_time"
    )
      .then((res) => {
        if (res.status === RESPONSE_CODE.SUCCESS) {
          setEvents(res.data?.events as Event[])
        } else {
          setShowToast({ show: true, text: t("failed_get_detail"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowToast({ show: true, text: t("failed_get_detail"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const handleSelectItem = (item: Event) => {
    setSelectedItem(item)
    setOpenJoinModal(true)
  }

  return (
    <>
      <Title title={t("adviser_info")} sub_title={t("adviser_detail")} />
      <Container
        maxWidth="lg"
        sx={{ px: "0px !important", pt: "0px !important" }}
        className="main-body-with-footer"
      >
        {isPC && (
          <Typography.SubHeadNormal
            sx={{ mt: "30px", ml: "20px", cursor: "pointer" }}
            onClick={() => navigate("/adviser")}
          >
            {t("back")}
          </Typography.SubHeadNormal>
        )}
        <Box maxWidth="md" sx={{ mx: "auto" }}>
          <Box className="adviser-abstract-container" sx={{ mt: "20px" }}>
            <Image
              src={adviser ? (adviser?.photoUrl as string) : ""}
              sx={{
                width: isPC ? "300px" : "100%",
                height: "400px",
                bgcolor: "divider",
              }}
              imageStyle={{ width: "100%", height: "100%" }}
              alt=""
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                p: isPC ? "20px" : "20px 0px 0px 0px",
                width: isPC ? "calc(100% - 420px)" : "100%",
              }}
            >
              <Typography.Large>
                {adviser
                  ? `${adviser?.lastName} ${adviser?.firstName}${
                      adviser.isResident ? `(${t("resident_adviser")})` : ""
                    }`
                  : ""}
              </Typography.Large>
              <Typography.SubHeadNormal sx={{ mt: "20px", fontFamily: "Noto Sans JP !important" }}>
                {getJobTitles(adviser)}
              </Typography.SubHeadNormal>
              {loggedin ? (
                <>
                  {userType === MEMBER_TYPE.USER && (
                    <Button
                      variant="contained"
                      sx={{
                        width: "240px",
                        py: "16px",
                        fontSize: "1rem",
                        fontFamily: "Noto Sans JP !important",
                        lineHeight: "1rem",
                        fontWeight: 700,
                        mt: "20px",
                        display: "flex",
                        justifyContent: "center",
                        borderRadius: 0,
                        ":hover": {
                          bgcolor: "#2DA389",
                        },
                      }}
                      onClick={() =>
                        navigate("/adviser/reservation", { state: { adviserID: adviser?.userId } })
                      }
                    >
                      {t("advise_with_this")}
                    </Button>
                  )}
                </>
              ) : (
                <Button
                  variant="contained"
                  sx={{
                    width: "240px",
                    py: "16px",
                    fontSize: "1rem",
                    fontFamily: "Noto Sans JP !important",
                    lineHeight: "1rem",
                    fontWeight: 700,
                    mt: "20px",
                    display: "flex",
                    justifyContent: "center",
                    borderRadius: 0,
                    ":hover": {
                      bgcolor: "#2DA389",
                    },
                  }}
                  onClick={() => navigate("/signup/type")}
                >
                  {t("register_to_join")}
                </Button>
              )}
            </Box>
          </Box>
          <Box className="adviser-detail-item">
            <Typography.SubHeadNormal>{t("abstract")}</Typography.SubHeadNormal>
            <Typography.InputFieldTitle sx={{ mt: "20px" }} lineHeight="24px">
              {adviser?.pr}
            </Typography.InputFieldTitle>
          </Box>
          <Box className="adviser-detail-item" sx={{ bgcolor: "#FBFAF4" }}>
            <Typography.SubHeadNormal>{t("message")}</Typography.SubHeadNormal>
            <Typography.InputFieldTitle sx={{ mt: "20px" }} lineHeight="24px">
              {adviser?.message}
            </Typography.InputFieldTitle>
          </Box>
          <Box className="adviser-detail-item">
            <Typography.SubHeadNormal>{t("career")}</Typography.SubHeadNormal>
            <Box sx={{ mt: "20px" }}>
              {adviser?.career?.map((career, index) => {
                return <CareerItem item={career} key={index} />
              })}
            </Box>
          </Box>
          {loggedin ? (
            <>
              {userType === MEMBER_TYPE.USER && (
                <Button
                  variant="contained"
                  sx={{
                    width: "240px",
                    py: "16px",
                    fontSize: "1rem",
                    fontFamily: "Noto Sans JP !important",
                    lineHeight: "1rem",
                    fontWeight: 700,
                    mt: "20px",
                    mx: "auto",
                    display: "flex",
                    justifyContent: "center",
                    borderRadius: 0,
                    ":hover": {
                      bgcolor: "#2DA389",
                    },
                  }}
                  onClick={() =>
                    navigate("/adviser/reservation", { state: { adviserID: adviser?.userId } })
                  }
                >
                  {t("advise_with_this")}
                </Button>
              )}
            </>
          ) : (
            <Button
              variant="contained"
              sx={{
                width: "240px",
                py: "16px",
                fontSize: "1rem",
                fontFamily: "Noto Sans JP !important",
                lineHeight: "1rem",
                fontWeight: 700,
                mt: "20px",
                mx: "auto",
                display: "flex",
                justifyContent: "center",
                borderRadius: 0,
                ":hover": {
                  bgcolor: "#2DA389",
                },
              }}
              onClick={() => navigate("/signup/type")}
            >
              {t("register_to_join")}
            </Button>
          )}
          <Typography.Large sx={{ mt: "80px", p: isPC ? "0px" : "10px" }}>
            {t("event_list")}
          </Typography.Large>
          {events.length === 0 ? (
            <Box sx={{ display: "flex", my: "50px", width: "100%" }}>
              <Typography.SubTitleNormal
                sx={{ mx: "auto", fontFamily: "Noto Sans JP !important", color: "gray" }}
              >
                {t("no_data")}
              </Typography.SubTitleNormal>
            </Box>
          ) : (
            <Grid container sx={{ mt: "30px", mb: "30px" }}>
              {events.map((event, index) => {
                return (
                  <Grid item xs={12} sm={12} md={4} key={index}>
                    <EventItem item={event} handleSelected={() => handleSelectItem(event)} />
                  </Grid>
                )
              })}
            </Grid>
          )}
        </Box>
      </Container>
      <EventJoinModal
        open={openJoinModal}
        handleOpen={setOpenJoinModal}
        item={selectedItem}
        refreshEvents={() => handleGetEvents()}
      />
    </>
  )
}
